import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Gap, Icon, Spacing, Text } from '@reservamos/elements';
import './CounterSeat.scss';
import usePricingBeforeCheckout from 'hooks/usePricingBeforeCheckout';
import LocalizedCurrency from 'components/LocalizedCurrency';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import BusIconIndicator from './BusIconIndicator';

/**
 * It shows the selected seats
 * @param {*} param0 - Props
 * @param {node} param0.children - Children
 * @param {boolean} param0.hasPetFriendly - If selected seats includes a pet friendly seat
 * @param {boolean} param0.hasScale - provitional prop
 * @returns - CounterSeat component.
 */
const CounterSeat = ({
  children,
  hasPetFriendly,
  roundTrip,
  hasScale,
  firstTripIsActive,
  secondTripIsActive,
  routeLabel,
}) => {
  const { t } = useTranslation('seats');
  const features = useWhitelabelFeatures();
  const showSeatPriceMobile = features.SEAT_PRICE_TOTAL_MOBILE;
  const { purchaseTotal } = usePricingBeforeCheckout({ isRoundTrip: roundTrip });

  return (
    <div className="counter-seat">
      <Spacing fullWidth vertical size="XS">
        <div className="relative flex flex-row justify-between w-full items-center">
          <Gap alignItems="center" columnGap="M">
            <div>
              <Spacing size="S">
                <div className="h-[35px] flex items-center">
                  <Text size="L" weight="bold">
                    {t('pet_counter.title')}
                  </Text>
                </div>
                {hasScale ? (
                  <Spacing size="XS">
                    <BusIconIndicator isActive={firstTripIsActive} />
                    <BusIconIndicator isActive={secondTripIsActive} />
                  </Spacing>
                ) : (
                  <Icon type="emojiBusSide" />
                )}
              </Spacing>
              {hasScale && <Text size="S">{routeLabel}</Text>}
            </div>

            {children}
          </Gap>
          {showSeatPriceMobile && (
            <div className="flex md:hidden self-start h-[35px] items-center pl-3">
              <LocalizedCurrency
                weight="semibold"
                color="accent"
                size="L"
                price={purchaseTotal}
                currencyStyle="currency"
              />
            </div>
          )}
        </div>

        {hasPetFriendly && (
          <Gap alignItems="center" columnGap="XXS">
            <Icon type="emojiPawsPet" size="S" />
            <Icon type="emojiPerson" size="S" />

            <Text size="S" mobileSize="XS" weight="semibold" color="grayMedium">
              {t('pet_counter.message')}
            </Text>
          </Gap>
        )}
      </Spacing>
    </div>
  );
};

CounterSeat.propTypes = {
  children: PropTypes.node.isRequired,
  hasPetFriendly: PropTypes.bool,
  hasScale: PropTypes.bool,
  firstTripIsActive: PropTypes.bool,
  roundTrip: PropTypes.bool,
  secondTripIsActive: PropTypes.bool,
  routeLabel: PropTypes.string,
};

CounterSeat.defaultProps = {
  hasPetFriendly: false,
  hasScale: false,
  firstTripIsActive: false,
  secondTripIsActive: false,
  routeLabel: '',
};

export default CounterSeat;
